// Turns a workflow into trail cards and arrows. Consecutive steps that just
// pass their table to the next step are grouped into one card, so a straight
// line of steps reads as a numbered list instead of a long chain of boxes.
import { stepInputs } from "../../../../engine";
import { DEFAULT_COLORS } from "./colors";

const COLUMN_WIDTH = 380;
const ROW_GAP = 40;
const CARD_HEIGHT = { source: 190, output: 170, stepLine: 46, stepHeader: 70 };

// Groups steps into chains. A chain breaks when a step reads from anything
// other than the step right before it, reads several tables, or when the
// previous step's table is also used elsewhere.
export const buildStepGroups = (workflow) => {
  const consumers = {};
  const addConsumer = (id) => {
    consumers[id] = (consumers[id] || 0) + 1;
  };
  workflow.steps.forEach((_, index) => stepInputs(workflow, index).forEach(addConsumer));
  workflow.outputs.forEach((output) => (output.sheets || []).forEach((sheet) => addConsumer(sheet.from)));

  const groups = [];
  workflow.steps.forEach((step, index) => {
    const inputs = stepInputs(workflow, index);
    const previous = workflow.steps[index - 1];
    const current = groups[groups.length - 1];
    const continues =
      current &&
      previous &&
      inputs.length === 1 &&
      inputs[0] === previous.id &&
      consumers[previous.id] === 1;
    if (continues) current.steps.push({ step, index });
    else groups.push({ id: `group-${step.id}`, inputs, steps: [{ step, index }] });
  });
  return groups;
};

export const buildGraph = (workflow) => {
  const groups = buildStepGroups(workflow);
  const nodeOfTable = {}; // table id -> trail node id
  workflow.sources.forEach((source) => {
    nodeOfTable[source.id] = source.id;
  });
  groups.forEach((group) => group.steps.forEach(({ step }) => (nodeOfTable[step.id] = group.id)));

  // Column = 1 + deepest input column.
  const depth = {};
  workflow.sources.forEach((source) => (depth[source.id] = 0));
  groups.forEach((group) => {
    depth[group.id] = 1 + Math.max(0, ...group.inputs.map((id) => depth[nodeOfTable[id]] ?? 0));
  });
  workflow.outputs.forEach((output) => {
    const from = (output.sheets || []).map((sheet) => depth[nodeOfTable[sheet.from]] ?? 0);
    depth[output.id] = 1 + Math.max(0, ...from);
  });

  const nextY = {};
  const place = (id, height) => {
    const column = depth[id];
    const y = nextY[column] ?? 0;
    nextY[column] = y + height + ROW_GAP;
    return { x: column * COLUMN_WIDTH, y };
  };

  const nodes = [
    ...workflow.sources.map((source) => ({
      id: source.id,
      type: "source",
      position: place(source.id, CARD_HEIGHT.source),
      data: { source, color: source.color || DEFAULT_COLORS.source },
    })),
    ...groups.map((group) => ({
      id: group.id,
      type: "steps",
      position: place(group.id, CARD_HEIGHT.stepHeader + group.steps.length * CARD_HEIGHT.stepLine),
      // A card's colour is stored on its first step.
      data: { group, color: group.steps[0].step.color || DEFAULT_COLORS.steps },
    })),
    ...workflow.outputs.map((output) => ({
      id: output.id,
      type: "outputFile",
      position: place(output.id, CARD_HEIGHT.output),
      data: { output, color: output.color || DEFAULT_COLORS.outputFile },
    })),
  ];

  // Table each card hands to the next: a source itself, or a group's last step.
  const tableOfNode = Object.fromEntries([
    ...workflow.sources.map((source) => [source.id, source.id]),
    ...groups.map((group) => [group.id, group.steps[group.steps.length - 1].step.id]),
  ]);

  // Links take the colour of the card they come from unless given their own
  // (stored on the receiving step/output as linkColors[fromTable]).
  const nodeColor = Object.fromEntries(nodes.map((node) => [node.id, node.data.color]));
  const edges = [];
  const addEdge = (fromTable, toNode, target, linkColors) => {
    const source = nodeOfTable[fromTable];
    if (!source) return;
    const id = `${source}->${toNode}:${fromTable}`;
    const customColor = linkColors?.[fromTable];
    if (!edges.some((e) => e.id === id)) {
      edges.push({ id, source, target: toNode, data: { tableId: fromTable, ...target, customColor, color: customColor || nodeColor[source] } });
    }
  };
  groups.forEach((group) => {
    const first = group.steps[0].step;
    group.inputs.forEach((input) => addEdge(input, group.id, { kind: "step", stepId: first.id }, first.linkColors));
  });
  workflow.outputs.forEach((output) =>
    (output.sheets || []).forEach((sheet) => addEdge(sheet.from, output.id, { kind: "output", outputId: output.id }, output.linkColors)),
  );

  return { nodes, edges, groups, tableOfNode };
};
